import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useAuth } from '@/contexts/AuthContext';
import MethodologySection from '@/components/MethodologySection';
import { Rocket, TrendingUp, Heart, ShieldCheck, HelpCircle, ArrowRight, BarChart2, Sparkles, Award, ChevronDown } from 'lucide-react';

const Index = () => {
  const { t } = useLanguage();
  const { user } = useAuth();

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header with glassmorphism effect */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-gray-100 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Logo size="md" />
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            {user ? (
              <Link to="/dashboard">
                <Button 
                  variant="default" 
                  size="sm"
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all duration-300"
                >
                  {t('nav.dashboard')}
                </Button>
              </Link>
            ) : (
              <Link to="/auth">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-blue-200 hover:bg-blue-50 text-blue-700 transition-all duration-300"
                >
                  {t('common.login')}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero with improved visual appeal */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-white to-blue-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-1/2 text-left space-y-6">
              <div className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-wider mb-2">
                {t('index.tagline') || "Emotion-Driven Analytics"}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 leading-tight">
                {t('index.heroTitle')}
              </h1>
              <p className="text-lg text-gray-600 max-w-xl">
                {t('index.heroSubtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/industry">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 w-full sm:w-auto"
                  >
                    {t('index.startButton')} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="lg"
                  className="px-8 py-6 bg-white hover:bg-blue-50 text-blue-700 border border-blue-200 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 w-full sm:w-auto"
                  onClick={() => scrollToSection('why-beamevaluate')}
                >
                  <HelpCircle className="mr-2 h-5 w-5" />
                  {t('index.whyBeamevaluateButton')}
                </Button>
              </div>
            </div>
            <div className="md:w-1/2 mt-10 md:mt-0 flex justify-center">
              <div className="relative">
                <div className="absolute -top-10 -left-10 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
                <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
                <div className="absolute inset-0 w-64 h-64 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
                <div className="relative">
                  <div className="bg-white rounded-2xl shadow-xl p-4 md:p-8 backdrop-blur-sm bg-opacity-80 border border-gray-100">
                    <BarChart2 className="h-24 w-24 mb-4 text-purple-600 mx-auto" strokeWidth={1.5} />
                    <div className="text-center space-y-4">
                      <h3 className="text-2xl font-bold text-gray-800">BEAM Score</h3>
                      <div className="flex justify-center gap-6">
                        <div>
                          <div className="text-3xl font-bold text-purple-600">8.7</div>
                          <div className="text-sm text-gray-500">NEV</div>
                        </div>
                        <div className="h-12 border-l border-gray-200"></div>
                        <div>
                          <div className="text-3xl font-bold text-blue-600">4.2</div>
                          <div className="text-sm text-gray-500">EROI</div>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="text-sm text-gray-500">Next level insights for your business</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certification badges - Enhanced with one-liner */}
      <section className="py-10 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-6">
            <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent inline-block">
              Emotional Intelligence Quantified & Certified
            </h2>
            <p className="text-gray-600 mt-2 max-w-3xl mx-auto">
              The BEAM Score is our proprietary metric that translates emotional resonance into actionable business intelligence, with tiered certifications to recognize excellence.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6 md:gap-12 items-center mt-6">
            <div className="flex items-center">
              <Award className="h-8 w-8 text-amber-500 mr-2" />
              <div>
                <div className="text-sm text-gray-500">BEAM Gold</div>
                <div className="text-base font-semibold text-amber-600">90-100</div>
              </div>
            </div>
            <div className="flex items-center">
              <Award className="h-8 w-8 text-gray-400 mr-2" />
              <div>
                <div className="text-sm text-gray-500">BEAM Silver</div>
                <div className="text-base font-semibold text-gray-600">80-89</div>
              </div>
            </div>
            <div className="flex items-center">
              <Award className="h-8 w-8 text-amber-700 mr-2" />
              <div>
                <div className="text-sm text-gray-500">BEAM Bronze</div>
                <div className="text-base font-semibold text-amber-700">70-79</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why BEAMEvaluate - Redesigned with cards and icons */}
      <section 
        id="why-beamevaluate" 
        className="py-20 bg-white"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {t('index.whyBeamevaluate')}
            </h2>
            <p className="text-gray-700 max-w-3xl mx-auto">
              {t('index.whyDescription')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 group hover:-translate-y-1">
              <div className="bg-blue-100 rounded-full p-4 mb-6 w-16 h-16 flex items-center justify-center group-hover:bg-blue-200 transition-all duration-300">
                <Rocket className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-800">{t('index.beyondGrowthMetrics')}</h3>
              <p className="text-gray-600">{t('index.beyondGrowthDesc')}</p>
            </div>
            
            <div className="bg-gradient-to-br from-white to-red-50 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 group hover:-translate-y-1">
              <div className="bg-red-100 rounded-full p-4 mb-6 w-16 h-16 flex items-center justify-center group-hover:bg-red-200 transition-all duration-300">
                <Heart className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-800">{t('index.emotionalIntelligence')}</h3>
              <p className="text-gray-600">{t('index.emotionalIntelligenceDesc')}</p>
            </div>
            
            <div className="bg-gradient-to-br from-white to-purple-50 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 group hover:-translate-y-1">
              <div className="bg-purple-100 rounded-full p-4 mb-6 w-16 h-16 flex items-center justify-center group-hover:bg-purple-200 transition-all duration-300">
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-800">{t('index.scientificValidation')}</h3>
              <p className="text-gray-600">{t('index.scientificValidationDesc')}</p>
            </div>
            
            <div className="bg-gradient-to-br from-white to-amber-50 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 group hover:-translate-y-1">
              <div className="bg-amber-100 rounded-full p-4 mb-6 w-16 h-16 flex items-center justify-center group-hover:bg-amber-200 transition-all duration-300">
                <ShieldCheck className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-800">{t('index.holisticAssessment')}</h3>
              <p className="text-gray-600">{t('index.holisticAssessmentDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits - Redesigned with modern cards */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-800">
            {t('index.keyBenefitsTitle')}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-500"></div>
              <div className="p-8">
                <div className="mb-6 bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-all">
                  <Sparkles className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gray-800">
                  {t('index.keyBenefit1')}
                </h3>
                <p className="text-gray-600 text-base">
                  {t('index.keyBenefit1Description') || 'Discover how your brand resonates emotionally with your audience and where it falls on the NEV scale.'}
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-500"></div>
              <div className="p-8">
                <div className="mb-6 bg-purple-100 w-14 h-14 rounded-full flex items-center justify-center group-hover:bg-purple-200 transition-all">
                  <BarChart2 className="h-7 w-7 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gray-800">
                  {t('index.keyBenefit2')}
                </h3>
                <p className="text-gray-600 text-base">
                  {t('index.keyBenefit2Description') || 'Get data-driven insights on specific areas where your brand can establish stronger emotional connections.'}
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="h-2 bg-gradient-to-r from-pink-500 to-red-500"></div>
              <div className="p-8">
                <div className="mb-6 bg-red-100 w-14 h-14 rounded-full flex items-center justify-center group-hover:bg-red-200 transition-all">
                  <TrendingUp className="h-7 w-7 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gray-800">
                  {t('index.keyBenefit3')}
                </h3>
                <p className="text-gray-600 text-base">
                  {t('index.keyBenefit3Description') || 'Compare your emotional performance metrics with industry standards and leading competitors.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology Section - Improved version */}
      <section id="methodology" className="py-20 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-blue-500/5 to-purple-500/5">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                {t('methodology.title')}
              </h2>
              <p className="text-center text-gray-600 mb-0">
                {t('methodology.subtitle')}
              </p>
              <div className="flex justify-center mt-8 mb-4">
                <Button 
                  variant="outline" 
                  className="text-gray-600 border-gray-300 flex items-center gap-2"
                  onClick={() => scrollToSection('methodology-details')}
                >
                  {t('index.learnMoreMethodology') || "Learn more about our methodology"}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div id="methodology-details" className="p-6">
              <MethodologySection />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - Redesigned with cards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-800">
            {t('index.testimonialsTitle')}
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-blue-50 rounded-xl p-8 border border-blue-100 shadow-md hover:shadow-lg transition-all duration-300 relative">
              <div className="absolute -top-5 left-10 bg-blue-100 w-10 h-10 flex items-center justify-center rounded-full">
                <span className="text-blue-600 text-xl font-serif">"</span>
              </div>
              <p className="italic mb-6 text-gray-700 pt-4">"{t('index.testimonial1')}"</p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-200 rounded-full flex items-center justify-center text-blue-600 font-semibold">SJ</div>
                <div className="ml-4">
                  <p className="font-semibold text-gray-800">Sarah J.</p>
                  <p className="text-sm text-gray-500">Marketing Director</p>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 rounded-xl p-8 border border-purple-100 shadow-md hover:shadow-lg transition-all duration-300 relative">
              <div className="absolute -top-5 left-10 bg-purple-100 w-10 h-10 flex items-center justify-center rounded-full">
                <span className="text-purple-600 text-xl font-serif">"</span>
              </div>
              <p className="italic mb-6 text-gray-700 pt-4">"{t('index.testimonial2')}"</p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-purple-200 rounded-full flex items-center justify-center text-purple-600 font-semibold">MT</div>
                <div className="ml-4">
                  <p className="font-semibold text-gray-800">Michael T.</p>
                  <p className="text-sm text-gray-500">Brand Strategist</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA section - new section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t('index.ctaTitle') || "Ready to discover your BEAM Score?"}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('index.ctaDescription') || "Start your evaluation today and gain valuable insights into your business's emotional and strategic metrics."}
          </p>
          <Link to="/industry">
            <Button 
              size="lg"
              className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {t('index.startEvaluation') || "Start Free Evaluation"} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer - Redesigned */}
      <footer className="py-12 bg-gray-800 text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between mb-10">
            <div className="mb-8 md:mb-0">
              <Logo size="lg" />
              <p className="mt-4 text-gray-400 max-w-md">
                {t('index.footerDescription') || "BEAM Evaluate provides business leaders with emotional and strategic metrics to make better decisions."}
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-lg font-semibold mb-4 text-blue-300">Product</h3>
                <ul className="space-y-2">
                  <li><Link to="/industry" className="text-gray-400 hover:text-white transition-colors">Get Started</Link></li>
                  <li><Link to="#methodology" className="text-gray-400 hover:text-white transition-colors">Methodology</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4 text-blue-300">Company</h3>
                <ul className="space-y-2">
                  <li><Link to="#" className="text-gray-400 hover:text-white transition-colors">About Us</Link></li>
                  <li><Link to="#" className="text-gray-400 hover:text-white transition-colors">Contact</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4 text-blue-300">Legal</h3>
                <ul className="space-y-2">
                  <li><Link to="#" className="text-gray-400 hover:text-white transition-colors">Privacy</Link></li>
                  <li><Link to="#" className="text-gray-400 hover:text-white transition-colors">Terms</Link></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-700 text-center text-gray-500">
            <p>{t('index.footer') || `© ${new Date().getFullYear()} BEAMEvaluate. All rights reserved.`}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
